import React, { useEffect, useState } from 'react';
import classNames from '../../../helper/classNames';
import {
  Modal,
  ModalBody,
  ModalTitle,
  ModalWindow,
} from '../../../components/Modal';
import CopyToClipboard from 'react-copy-to-clipboard';
import { CopyIcon } from '../../../components/icons/CopyIcon';
import { DownloadIcon } from '../../../components/icons/DownloadIcon';
import { downloadGCSFile } from './PublishMenus';
import ReactPlayer from 'react-player';
import { TwitterIcon } from '../../../components/icons/TwitterIcon';
import { Button } from '../../../components/common/Button';
import { PostTextAreaInput } from './PostTextAreaInput';
import { useTrackActivity } from '../../../hooks/track';

const createTweetURL = (text) => {
  return 'https://twitter.com/intent/tweet?text=' + encodeURIComponent(text);
};
export const SoloUploadToTwitterModal = ({
  setShowPublishModal,
  aspectRatio,
  saveHighlightSocialMediaPost,
  shared,
  series,
  highlights,
}) => {
  const highlight = highlights?.[0];
  const {
    title,
    HIGHLIGHT_SOCIAL_MEDIA_POSTS,
    HIGHLIGHT_THUMBNAIL,
    highlightId,
  } = highlight || {};
  const { socialMediaPosts } = HIGHLIGHT_SOCIAL_MEDIA_POSTS || {};
  const { twitter } = socialMediaPosts || {};
  const { text: description = '' } = twitter || {};
  const thumbnailUrl = HIGHLIGHT_THUMBNAIL?.imageUrl;
  const playbackUrl = highlight?.HIGHLIGHT_EDITED_LANDSCAPE?.playbackUrl;
  const videoGCSUrl = highlight?.HIGHLIGHT_EDITED_LANDSCAPE?.downloadUrl;

  const { title: episodeTitle } = series || {};

  const [editedDescription, setEditedDescription] = useState(description || '');
  const [isSavingPostTitleLoading, setIsSavingPostTitleLoading] =
    useState(false);
  const isDescriptionEdited = editedDescription.trim() !== description.trim();
  const [isDownloadVideoLoading, setIsDownloadVideoLoading] = useState(false);
  const [errorDownloading, setErrorDownloading] = useState(null);

  const track = useTrackActivity();

  useEffect(() => {
    track('Content Kit: Twitter Post Opened', { groupId: highlightId });
  }, [highlightId, track]);

  return (
    <ModalWindow
      size={Modal.size.xl}
      showCancel
      onCancel={() => {
        setShowPublishModal(null);
      }}
    >
      <ModalTitle>{title}</ModalTitle>
      <ModalBody>
        <div className="flex gap-[25px]">
          <div
            className={classNames(
              'flex flex-col gap-5 shrink-0',
              aspectRatio === 'portrait' ? 'flex-1' : 'w-1/4'
            )}
          >
            <div className="flex flex-col gap-2.5">
              <div className="flex justify-between">
                <span className="text-blue-dark text-sm font-medium">
                  Description
                </span>
                <div className="flex gap-1">
                  {!shared && isDescriptionEdited && (
                    <Button
                      padding={Button.padding.NONE}
                      color={Button.colors.PURPLE}
                      border={Button.border.ROUNDED}
                      onClick={async () => {
                        track('Content Kit: Twitter Post Description Updated', {
                          groupId: highlightId,
                        });
                        await saveHighlightSocialMediaPost(
                          setIsSavingPostTitleLoading,
                          editedDescription,
                          highlightId,
                          'twitter'
                        );
                      }}
                      state={
                        isSavingPostTitleLoading
                          ? Button.states.LOADING
                          : Button.states.DEFAULT
                      }
                    >
                      <div className="text-xxs h-5 flex justify-center items-center px-2.5">
                        Save
                      </div>
                    </Button>
                  )}
                  <CopyToClipboard text={editedDescription}>
                    <button>
                      <CopyIcon />
                    </button>
                  </CopyToClipboard>
                </div>
              </div>
              <PostTextAreaInput
                resizable={false}
                onChange={(event, value) => {
                  setEditedDescription(value);
                }}
                value={editedDescription}
                rows={6}
              />
            </div>
          </div>
          <div
            className={classNames(
              'flex flex-col gap-[15px]',
              aspectRatio === 'portrait' ? 'w-1/4' : 'flex-1'
            )}
          >
            <div className="flex gap-[5px] justify-end">
              {description && (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="text-xxs font-medium p-2 flex gap-[5px] rounded items-center"
                  href={createTweetURL(description)}
                >
                  Tweet
                  <TwitterIcon className="fill-white" />
                </a>
              )}
              {videoGCSUrl && (
                <Button
                  padding={Button.padding.NONE}
                  color={Button.colors.PURPLE}
                  onClick={() => {
                    track('Content Kit: Twitter Video Downloaded', {
                      groupId: highlightId,
                    });
                    downloadGCSFile(
                      videoGCSUrl,
                      `${episodeTitle}-${aspectRatio}.mp4`,
                      setIsDownloadVideoLoading,
                      setErrorDownloading
                    );
                  }}
                  state={
                    isDownloadVideoLoading
                      ? Button.states.LOADING
                      : Button.states.DEFAULT
                  }
                >
                  <div className="bg-purple text-white text-xxs font-medium flex gap-[5px] rounded items-center h-min  px-2">
                    <DownloadIcon height={12} className="fill-white" />
                    Video
                  </div>
                </Button>
              )}
              {thumbnailUrl && (
                <button
                  type="button"
                  className="bg-purple text-white text-xxs font-medium p-2 flex gap-[5px] rounded"
                  onClick={() => {
                    track('Content Kit: Twitter Thumbnail Download Clicked', {
                      groupId: highlightId,
                    });
                    downloadGCSFile(thumbnailUrl, `${title}.png`);
                  }}
                >
                  <DownloadIcon height={12} className="fill-white" />
                  Thumbnail
                </button>
              )}
            </div>
            {errorDownloading && (
              <div className="text-red text-xs font-light text-right">
                {errorDownloading}
              </div>
            )}
            {aspectRatio === 'portrait' ? (
              <style>{`.react-player > video { aspect-ratio: 9/16}`}</style>
            ) : null}
            <ReactPlayer
              className={classNames('react-player aspect-video')}
              width={'100%'}
              height="auto"
              onPlay={() => {
                track('Content Kit: Twitter Video Played', {
                  groupId: highlightId,
                });
              }}
              url={playbackUrl}
              playing={!!thumbnailUrl}
              controls={true}
              light={thumbnailUrl}
              loop={false}
              volume={1}
              muted={false}
              pip={false}
            />
          </div>
        </div>
      </ModalBody>
    </ModalWindow>
  );
};
